import React, { useState, useEffect } from "react";
import AdminLayout from "./layout/AdminLayout";
import API_BASE, { UPLOADS_BASE } from "../api";
import ImageUploadDropzone from "./components/ImageUploadDropzone";
import "./productmanager.css";

function CoconutManager() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [selectedProduct, setSelectedProduct] = useState(null);

  const [form, setForm] = useState({
    title: "",
    description: "",
    net_weight: "",
    image: null
  });

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  const loadProducts = async () => {
    try {
      const res = await fetch(`${API_BASE}/coconut_manager/get_coconut_products.php`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setProducts(data);
      } else if (data && Array.isArray(data.products)) {
        setProducts(data.products);
      }
    } catch (err) {
      console.error("Error loading coconut products:", err);
      showToast("error", "Failed to load coconut products.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setForm({
      title: "",
      description: "",
      net_weight: "",
      image: null
    });
  };

  const addProduct = async (e) => {
    if (e) e.preventDefault();

    if (!form.title.trim()) {
      showToast("error", "Coconut product title is required.");
      return;
    }

    setSubmitting(true);
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("description", form.description);
    fd.append("net_weight", form.net_weight);

    if (form.image) {
      fd.append("image", form.image);
    }

    try {
      const res = await fetch(`${API_BASE}/coconut_manager/add_coconut_product.php`, {
        method: "POST",
        body: fd
      });
      const data = await res.json();

      if (!data.success) {
        throw new Error(data.message || "Failed to add coconut product");
      }

      showToast("success", "Coconut product added successfully!");
      setShowAddModal(false);
      resetForm();
      loadProducts();
    } catch (err) {
      console.error("Add coconut error:", err);
      showToast("error", err.message || "Failed to add coconut product");
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (p) => {
    setSelectedProduct(p);
    setForm({
      title: p.title || "",
      description: p.description || "",
      net_weight: p.net_weight || "",
      image: null
    });
    setShowEditModal(true);
  };

  const updateProduct = async (e) => {
    if (e) e.preventDefault();

    if (!form.title.trim()) {
      showToast("error", "Product title is required.");
      return;
    }

    setSubmitting(true);
    const fd = new FormData();
    fd.append("id", selectedProduct.id);
    fd.append("title", form.title);
    fd.append("description", form.description);
    fd.append("net_weight", form.net_weight);

    if (form.image) {
      fd.append("image", form.image);
    }

    try {
      const res = await fetch(`${API_BASE}/coconut_manager/update_coconut_product.php`, {
        method: "POST",
        body: fd
      });
      const data = await res.json();

      if (!data.success) {
        throw new Error(data.message || "Failed to update coconut product");
      }

      showToast("success", "Coconut product updated successfully!");
      setShowEditModal(false);
      resetForm();
      loadProducts();
    } catch (err) {
      console.error("Update error:", err);
      showToast("error", err.message || "Failed to update coconut product");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteProduct = async () => {
    if (!selectedProduct) return;
    setSubmitting(true);

    try {
      const fd = new FormData();
      fd.append("id", selectedProduct.id);

      const res = await fetch(`${API_BASE}/coconut_manager/delete_coconut_product.php`, {
        method: "POST",
        body: fd
      });
      const data = await res.json();

      if (data && data.success === false) {
        throw new Error(data.message || "Delete failed");
      }

      showToast("success", "Product deleted successfully");
      setShowDeleteModal(false);
      loadProducts();
    } catch (err) {
      console.error("Delete error:", err);
      showToast("error", err.message || "Failed to delete coconut product");
    } finally {
      setSubmitting(false);
    }
  };

  const getProductImgUrl = (imageName) => {
    if (!imageName) return null;
    return `${UPLOADS_BASE}/coconut_products/${imageName}`;
  };

  return (
    <AdminLayout>
      <div className="pm-container">
        {/* Header */}
        <div className="pm-header">
          <div className="pm-header-info">
            <h2>Coconut Products Management</h2>
            <p>Manage organic Ceylon coconut oils, milk powders, and value-added items</p>
          </div>
          <button
            className="pm-add-btn"
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
          >
            <i className="fas fa-plus"></i> Add New Coconut Product
          </button>
        </div>

        {/* Toast Notification */}
        {toast && (
          <div className={`pm-toast ${toast.type === "success" ? "pm-toast-success" : "pm-toast-error"}`}>
            <i className={`fas ${toast.type === "success" ? "fa-check-circle" : "fa-exclamation-circle"}`}></i>
            <span>{toast.message}</span>
          </div>
        )}

        {/* Product Grid */}
        {loading ? (
          <div className="loader"></div>
        ) : products.length === 0 ? (
          <div className="pm-empty-state">
            <i className="fas fa-cookie"></i>
            <h3>No coconut products found</h3>
            <p>Click "Add New Coconut Product" to create your first item.</p>
          </div>
        ) : (
          <div className="pm-grid">
            {products.map((p) => {
              const imgUrl = getProductImgUrl(p.image);
              return (
                <div className="pm-card" key={p.id}>
                  <div className="pm-card-img-wrapper">
                    {imgUrl ? (
                      <img
                        src={imgUrl}
                        alt={p.title}
                        className="pm-img"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className="pm-img-placeholder"
                      style={{ display: imgUrl ? 'none' : 'flex' }}
                    >
                      <i className="fas fa-cookie"></i>
                      <span>No Image</span>
                    </div>
                    {p.net_weight && <span className="pm-weight-tag">{p.net_weight}</span>}
                  </div>

                  <div className="pm-card-body">
                    <h3 className="pm-name">{p.title}</h3>
                    <p className="pm-sub">{p.description || "Ceylon Coconut Product"}</p>

                    <div className="pm-actions">
                      <button
                        className="pm-edit"
                        onClick={() => openEdit(p)}
                        title="Edit Coconut Product"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="pm-delete"
                        onClick={() => {
                          setSelectedProduct(p);
                          setShowDeleteModal(true);
                        }}
                        title="Delete Coconut Product"
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ADD MODAL */}
        {showAddModal && (
          <div className="pm-modal-overlay" onClick={() => !submitting && setShowAddModal(false)}>
            <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pm-modal-header">
                <h2>Add New Coconut Product</h2>
                <button
                  className="pm-modal-close"
                  onClick={() => setShowAddModal(false)}
                  disabled={submitting}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={addProduct}>
                <div className="pm-modal-body">
                  <div className="pm-form-horizontal">
                    <div className="pm-left">
                      <div className="pm-form-group">
                        <label className="pm-label">Product Title *</label>
                        <input
                          type="text"
                          name="title"
                          placeholder="e.g. Extra Virgin Cold Pressed Coconut Oil"
                          className="pm-input"
                          value={form.title}
                          onChange={handleFormChange}
                          required
                        />
                      </div>

                      <div className="pm-form-group">
                        <label className="pm-label">Net Weight / Volume</label>
                        <input
                          type="text"
                          name="net_weight"
                          placeholder="e.g. 500ml / 1L / 250g"
                          className="pm-input"
                          value={form.net_weight}
                          onChange={handleFormChange}
                        />
                      </div>

                      <div className="pm-form-group">
                        <label className="pm-label">Description</label>
                        <textarea
                          name="description"
                          placeholder="Extraction method, purity, culinary & cosmetic uses..."
                          className="pm-textarea"
                          value={form.description}
                          onChange={handleFormChange}
                        />
                      </div>
                    </div>

                    <div className="pm-right">
                      <ImageUploadDropzone
                        value={form.image}
                        label="Product Image (Auto-WebP)"
                        onChange={(file) => setForm({ ...form, image: file })}
                        onClear={() => setForm({ ...form, image: null })}
                      />
                    </div>
                  </div>
                </div>

                <div className="pm-modal-footer">
                  <button
                    type="button"
                    className="pm-cancel"
                    onClick={() => setShowAddModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="pm-save" disabled={submitting}>
                    {submitting && <span className="pm-btn-spinner" />}
                    <span>{submitting ? "Saving & Optimizing..." : "Save Product"}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* EDIT MODAL */}
        {showEditModal && selectedProduct && (
          <div className="pm-modal-overlay" onClick={() => !submitting && setShowEditModal(false)}>
            <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pm-modal-header">
                <h2>Edit Coconut Product</h2>
                <button
                  className="pm-modal-close"
                  onClick={() => setShowEditModal(false)}
                  disabled={submitting}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={updateProduct}>
                <div className="pm-modal-body">
                  <div className="pm-form-horizontal">
                    <div className="pm-left">
                      <div className="pm-form-group">
                        <label className="pm-label">Product Title *</label>
                        <input
                          type="text"
                          name="title"
                          className="pm-input"
                          value={form.title}
                          onChange={handleFormChange}
                          required
                        />
                      </div>

                      <div className="pm-form-group">
                        <label className="pm-label">Net Weight / Volume</label>
                        <input
                          type="text"
                          name="net_weight"
                          className="pm-input"
                          value={form.net_weight}
                          onChange={handleFormChange}
                        />
                      </div>

                      <div className="pm-form-group">
                        <label className="pm-label">Description</label>
                        <textarea
                          name="description"
                          className="pm-textarea"
                          value={form.description}
                          onChange={handleFormChange}
                        />
                      </div>
                    </div>

                    <div className="pm-right">
                      <ImageUploadDropzone
                        value={form.image}
                        existingUrl={getProductImgUrl(selectedProduct.image)}
                        label="Change Image (Auto-WebP)"
                        onChange={(file) => setForm({ ...form, image: file })}
                        onClear={() => setForm({ ...form, image: null })}
                      />
                    </div>
                  </div>
                </div>

                <div className="pm-modal-footer">
                  <button
                    type="button"
                    className="pm-cancel"
                    onClick={() => setShowEditModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="pm-save" disabled={submitting}>
                    {submitting && <span className="pm-btn-spinner" />}
                    <span>{submitting ? "Updating..." : "Update Product"}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* DELETE MODAL */}
        {showDeleteModal && selectedProduct && (
          <div className="pm-modal-overlay" onClick={() => !submitting && setShowDeleteModal(false)}>
            <div className="pm-modal-small" onClick={(e) => e.stopPropagation()}>
              <div className="pm-delete-icon">
                <i className="fas fa-trash-alt"></i>
              </div>
              <h3>Delete Coconut Product?</h3>
              <p>
                Are you sure you want to delete <strong>"{selectedProduct.title}"</strong>?
              </p>
              <div className="pm-delete-actions">
                <button
                  className="pm-cancel"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  className="pm-delete-confirm"
                  onClick={deleteProduct}
                  disabled={submitting}
                >
                  {submitting ? "Deleting..." : "Yes, Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default CoconutManager;
